import { motion } from "framer-motion";
import { MessageCircle, Phone, FileText } from "lucide-react";
import { portfolio } from "@/data/landingData";
import { useState } from "react";

const WHATSAPP_LINK = "https://wa.me/33643693688?text=Bonjour%2C%20je%20souhaite%20un%20devis%20pour%20une%20enseigne.";

const categories = [
  { id: "all", label: "Tous" },
  { id: "led", label: "Enseignes LED" },
  { id: "lettres", label: "Lettres Relief" },
  { id: "caisson", label: "Caissons" },
  { id: "facade", label: "Façades" },
];

// Map portfolio items to categories
const categorizedPortfolio = portfolio.map((item, idx) => {
  const type = item.type.toLowerCase();
  let cat = "led";
  if (type.includes("lettre") || type.includes("relief") || type.includes("découpé")) cat = "lettres";
  else if (type.includes("caisson") || type.includes("bandeau")) cat = "caisson";
  else if (type.includes("façade") || type.includes("angle") || type.includes("pose")) cat = "facade";
  return { ...item, category: cat };
});

export function PortfolioSection({ onScrollToForm }) {
  const [activeCategory, setActiveCategory] = useState("all");
  const filtered = activeCategory === "all" ? categorizedPortfolio : categorizedPortfolio.filter(p => p.category === activeCategory);

  return (
    <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-[#0a0a0a]" id="realisations" data-testid="portfolio-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-sm uppercase tracking-[0.3em] font-medium text-[#00FF66] mb-4 font-['Outfit',sans-serif]">NOS RÉALISATIONS</p>
          <h2 className="font-['Clash_Display',sans-serif] text-3xl sm:text-4xl font-semibold tracking-tight text-white">
            Plus de 500 enseignes installées
          </h2>
          <p className="mt-4 text-zinc-400 max-w-2xl mx-auto font-['Outfit',sans-serif]">
            Découvrez nos dernières créations. Chaque projet est conçu sur mesure.
          </p>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap justify-center gap-2 mb-10" data-testid="portfolio-categories">
          {categories.map(cat => (
            <button key={cat.id} onClick={() => setActiveCategory(cat.id)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                activeCategory === cat.id
                  ? "bg-[#00FF66] text-black font-bold"
                  : "bg-[#1a1a1a] text-zinc-400 hover:text-white border border-[#262626]"
              }`}
              data-testid={`cat-${cat.id}`}>
              {cat.label}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="portfolio-grid">
          {filtered.map((item, idx) => (
            <motion.div key={`${item.name}-${idx}`}
              layout
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className="group relative aspect-[4/5] rounded-2xl overflow-hidden cursor-pointer"
              data-testid={`portfolio-item-${idx}`}>
              <img src={item.image} alt={`${item.name} - ${item.type}`} loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300" />
              <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                <p className="text-white font-semibold text-sm font-['Outfit',sans-serif]">{item.name}</p>
                <p className="text-[#00FF66] text-xs">{item.type}</p>
              </div>
              <div className="absolute inset-0 border-2 border-transparent group-hover:border-[#00FF66]/50 rounded-2xl transition-colors duration-300" />
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12">
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 bg-[#25D366] text-white px-8 py-4 rounded-xl font-bold transition-all hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(37,211,102,0.3)]"
            data-testid="portfolio-whatsapp">
            <MessageCircle className="w-5 h-5" /> Discuter de mon projet
          </a>
          <button onClick={onScrollToForm}
            className="flex items-center gap-2 border border-[#00FF66] text-[#00FF66] px-8 py-4 rounded-xl font-bold transition-all hover:bg-[#00FF66] hover:text-black"
            data-testid="portfolio-devis">
            <FileText className="w-5 h-5" /> Demander un devis gratuit
          </button>
        </div>
      </div>
    </section>
  );
}
